import { morph, MORPH_OPTIONS } from '@theme/morph';
import { startViewTransition } from '@theme/utilities';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, {promise: Promise<string>, controller: AbortController, consumers: Set<symbol>, settled: boolean}>}
   */
  #pendingPromises = new Map();

  #pageLoadURL = window.location.href;

  #transitionQueue = Promise.resolve();

  constructor() {
    this.#cacheInitialCollection();
    window.addEventListener('DOMContentLoaded', this.#cacheInitialCollection.bind(this), { once: true });
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  refreshWishlistButtons(sectionId) {
    const section = document.getElementById(buildSectionSelector(sectionId));
    if (section?.querySelector('.product-grid')) restoreCollectionWishlist(section);
  }

  #cacheInitialCollection() {
    if (document.readyState === 'loading') return;
    const sections = window.__jpCollectionSections;
    if (!sections) return;
    for (const [sectionId, { url, html }] of sections) {
      const section = document.getElementById(sectionId);
      if (!section) continue;
      const initial = new DOMParser().parseFromString(html, 'text/html').querySelector('results-list');
      if (!initial) continue;
      const complete = section.cloneNode(true);
      complete.querySelector('results-list')?.replaceWith(initial);
      this.#cache.set(buildSectionRenderingURL(sectionId, new URL(url)), complete.outerHTML);
    }
    delete window.__jpCollectionSections;
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @param {boolean} [options.viewTransition] - Animate the ready section with the product grid transition
   * @param {() => boolean} [options.shouldRender] - Whether the caller still owns the current update
   * @returns {Promise<string | undefined>} The rendered section HTML, or undefined if already superseded
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full', viewTransition = false, shouldRender = () => true } = options ?? {};
    const { url } = options ?? {};
    if (!shouldRender()) return;
    const previousController = this.#abortControllersBySectionId.get(sectionId);
    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);
    const sectionRequest = this.getSectionHTML(sectionId, cache, url, {
      signal: abortController.signal,
      priority: 'high',
    });
    previousController?.abort();

    try {
      const sectionHTML = await sectionRequest;
      const update = () => {
        if (!abortController.signal.aborted && shouldRender()) return morphSection(sectionId, sectionHTML, { mode });
      };

      if (viewTransition) {
        const transition = this.#transitionQueue.then(() => {
          if (!abortController.signal.aborted && shouldRender()) return startViewTransition(update, ['product-grid']);
        });
        this.#transitionQueue = transition.catch(() => {});
        await transition;
      } else {
        await update();
      }

      return sectionHTML;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {{signal?: AbortSignal, priority?: RequestPriority}} [options] - Ownership of this caller's request
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), options = {}) {
    if (options.signal?.aborted) throw options.signal.reason;
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let request = this.#pendingPromises.get(sectionUrl);
    if (request?.controller.signal.aborted) request = undefined;

    if (!request && useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    if (!request) {
      const controller = new AbortController();
      request = { controller, consumers: new Set(), settled: false, promise: Promise.resolve('') };
      const current = request;
      current.promise = fetch(sectionUrl, { signal: controller.signal, priority: options.priority ?? 'auto' })
        .then(async (response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          if (!parsed.getElementById(`shopify-section-${sectionId.replace(/^shopify-section-/, '')}`)) {
            throw new Error(`Section ${sectionId} not found in the section rendering response`);
          }
          if (controller.signal.aborted) throw controller.signal.reason;
          this.#cache.set(sectionUrl, html);
          return html;
        })
        .finally(() => {
          current.settled = true;
          if (this.#pendingPromises.get(sectionUrl) === current) this.#pendingPromises.delete(sectionUrl);
        });
      this.#pendingPromises.set(sectionUrl, current);
    }

    const current = request;
    const consumer = Symbol();
    current.consumers.add(consumer);
    return new Promise((resolve, reject) => {
      const release = () => {
        options.signal?.removeEventListener('abort', abort);
        current.consumers.delete(consumer);
        if (!current.settled && current.consumers.size === 0) current.controller.abort();
      };
      const abort = () => { release(); reject(options.signal.reason); };
      options.signal?.addEventListener('abort', abort, { once: true });
      current.promise.then(
        (html) => { release(); resolve(html); },
        (error) => { release(); reject(error); }
      );
    });
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    if (window.location.href !== this.#pageLoadURL) return;
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

// Swym injects interactive buttons after rendering. Section responses do not
// contain them, and the app can skip products it has already initialized.
// Keep the original nodes (and their listeners) by product, including products
// temporarily excluded by a filter. A removed section releases its cache.
/** @type {WeakMap<Element, Map<string, Element>>} */
const collectionWishlistButtons = new WeakMap();
const collectionWishlistRefreshRevisions = new WeakMap();
const collectionWishlistPendingCards = new WeakSet();

function refreshCollectionWishlist(section) {
  const revision = (collectionWishlistRefreshRevisions.get(section) ?? 0) + 1;
  collectionWishlistRefreshRevisions.set(section, revision);
  const swat = window._swat;
  const api = swat?.collectionsApi;
  const selectors = window.SwymCollectionsConfig?.CollectionsSelectors;
  if (!Array.isArray(selectors) || !api?.fetchWishlistedProducts ||
      !api.getDuAndEpiOfWishlistedProducts || !api.renderWishlistButtonOnProductCards) return;

  const isCurrent = () => section.isConnected && collectionWishlistRefreshRevisions.get(section) === revision;
  const missingCards = () => [...section.querySelectorAll('.product-grid product-card[data-product-id]')]
    .filter(card => !collectionWishlistPendingCards.has(card) &&
      !card.querySelector(':scope > .swym-wishlist-collections-v2-container'));
  if (!missingCards().length) return;
  const enabled = swat.retailerSettings?.Wishlist?.EnableCollections;
  const report = error => console.warn('Could not refresh collection wishlist buttons.', error);

  try {
    api.fetchWishlistedProducts(data => {
      const render = async () => {
        if (!isCurrent()) return;
        const saved = api.getDuAndEpiOfWishlistedProducts(data, enabled, swat);
        for (const selector of selectors) {
          if (!isCurrent()) return;
          if (!selector.AttachButtonSelector || !selector.ProductUrlSelector) continue;
          const cards = missingCards().filter(card => card.matches(selector.AttachButtonSelector));
          if (!cards.length) continue;
          cards.forEach(card => collectionWishlistPendingCards.add(card));
          try {
            await api.renderWishlistButtonOnProductCards(cards, selector, saved, enabled, swat);
          } finally {
            cards.forEach(card => collectionWishlistPendingCards.delete(card));
            if (!isCurrent() && section.isConnected && cards.some(card => section.contains(card) &&
                !card.querySelector(':scope > .swym-wishlist-collections-v2-container'))) {
              refreshCollectionWishlist(section);
            }
          }
        }
      };
      render().catch(report);
    }, swat);
  } catch (error) {
    report(error);
  }
}

function restoreCollectionWishlist(section) {
  const buttons = collectionWishlistButtons.get(section);
  if (buttons) {
    for (const card of section.querySelectorAll('.product-grid product-card[data-product-id]')) {
      const button = buttons.get(card.getAttribute('data-product-id'));
      if (!button) continue;
      const renderedButton = card.querySelector(':scope > .swym-wishlist-collections-v2-container');
      if (renderedButton !== button) {
        renderedButton?.remove();
        card.append(button);
      }
    }
  }
  refreshCollectionWishlist(section);
}

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [options] - Additional options
 * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {() => void} [options.onRendered] - Called synchronously after a successful morph.
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  let wishlistButtons;
  if (mode === 'full' && existingElement.querySelector('.product-grid')) {
    wishlistButtons = collectionWishlistButtons.get(existingElement) ?? new Map();
    collectionWishlistButtons.set(existingElement, wishlistButtons);
    for (const card of existingElement.querySelectorAll('.product-grid product-card[data-product-id]')) {
      const button = card.querySelector(':scope > .swym-wishlist-collections-v2-container');
      if (button) wishlistButtons.set(card.getAttribute('data-product-id'), button);
    }
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });

  if (wishlistButtons) {
    restoreCollectionWishlist(existingElement);
  }

  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
  options.onRendered?.();
}

/**
 * Injects a `<style data-section-stylesheet>` from the parsed SFR response
 * into the live section wrapper. Replaces the existing stylesheet, if it exists.
 *
 * @param {Document} fragment - The parsed response document
 * @param {HTMLElement} sectionElement - The live section wrapper element
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
